import { computedAsync } from '@vueuse/core';
import { computed, reactive, ref } from 'vue';

import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import PersonalRest from '@exchange/libs/rest-api/personal-api';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';
import type { FuturesPublicTradingFlag } from '@exchange/libs/utils/launchdarkly/src/lib/defaults';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import futuresService, { getDefaultState as getDefaultFuturesServiceState } from './futures.service';

type FuturesFeatureFlagServiceState = {
  countryOfResidency: string | null | undefined;
};

const getDefaultState = (): FuturesFeatureFlagServiceState => ({
  countryOfResidency: null,
});

class FuturesFeatureFlagService {
  private readonly logPrefix = 'FuturesFeatureFlagService:';

  private state = reactive<FuturesFeatureFlagServiceState>(getDefaultState());

  private isFetchingCountryOfResidency = ref(false);

  private isFetchedCountryOfResidency = computed(() => this.state.countryOfResidency !== getDefaultState().countryOfResidency);

  private isFetchedClientCategorisation = computed(() => futuresService.isFetchedClientCategorisation.value);

  private isFetchedUser = computed(() => !!accountService.accountUser.value);

  private getCountryOfResidency = async () => {
    if (this.isFetchingCountryOfResidency.value) {
      return getDefaultState().countryOfResidency;
    }

    this.isFetchingCountryOfResidency.value = true;

    try {
      if (this.state.countryOfResidency) {
        return this.state.countryOfResidency;
      }

      const identity = await PersonalRest.Identity.get();

      this.state.countryOfResidency = identity.addressOfResidency.country;

      return this.state.countryOfResidency;
    } catch (error) {
      if ((error as unknown as { status: number }).status === 404) {
        return undefined;
      }

      return getDefaultState().countryOfResidency;
    } finally {
      this.isFetchingCountryOfResidency.value = false;
    }
  };

  private countryOfResidency = computedAsync(
    async () => {
      if (!authService.hasBeenAuthenticated) {
        return getDefaultState().countryOfResidency;
      }

      const countryOfResidency = await this.getCountryOfResidency();

      return countryOfResidency;
    },
    getDefaultState().countryOfResidency,
    { lazy: true },
  );

  private clientCategorisation = computedAsync(
    async () => {
      if (!authService.hasBeenAuthenticated) {
        return getDefaultFuturesServiceState().clientCategorisation;
      }

      const clientCategorisation = await futuresService.getClientCategorisation();

      return clientCategorisation;
    },
    getDefaultFuturesServiceState().clientCategorisation,
    { lazy: true },
  );

  public isEnabled = computed(() => launchdarkly.flags['futures-enabled'].value);

  public isReady = computed(() => {
    const value = launchdarkly.isAuthorizedFlagsReady.value && this.isFetchedCountryOfResidency.value && this.isFetchedClientCategorisation.value && this.isFetchedUser.value;

    logger.info(`${this.logPrefix} isReady`, {
      launchdarklyReady: launchdarkly.isAuthorizedFlagsReady.value,
      countryOfResidencyFetched: this.isFetchedCountryOfResidency.value,
      clientCategorisationFetched: this.isFetchedClientCategorisation.value,
      userFetched: this.isFetchedUser.value,
      value,
    });

    return value;
  });

  private publicTradingFlag = computed(() => launchdarkly.flags['futures-public-trading'].value as FuturesPublicTradingFlag);

  public isPublicTradingEnabled = computed(() => {
    const { enabled: publicTradingFlagEnabled, enabledForCountriesByCategorisation } = this.publicTradingFlag.value;
    let value: boolean;

    // If the public trading flag is disabled, or we don't have an account user, then public trading is disabled
    if (!publicTradingFlagEnabled || !accountService.accountUser.value) {
      value = false;
    }
    // If the user is not retail (institutional or market maker) or the public trading flag is enabled for all countries, then public trading is enabled
    else if (!accountService.accountUser.value.isRetail || !enabledForCountriesByCategorisation) {
      value = true;
    }
    // If the user is categorised, then check if their country of residency is in the list of enabled countries for their categorisation
    else if (this.clientCategorisation.value) {
      value = enabledForCountriesByCategorisation[this.clientCategorisation.value]?.includes(this.countryOfResidency.value as string) ?? false;
    }
    // eslint-disable-next-line max-len
    // Otherwise, the user must be retail, not classified, and the public trading flag is enabled for specific countries, so check if the country of residency is in the full list (retail + professional) of enabled countries
    else {
      const enabledForCountries = new Set(Object.values(enabledForCountriesByCategorisation).flat());

      value = enabledForCountries.has(this.countryOfResidency.value as string);
    }

    logger.info(`${this.logPrefix} isPublicTradingEnabled`, {
      enabled: this.publicTradingFlag.value.enabled,
      enabledForCountriesByCategorisation: this.publicTradingFlag.value.enabledForCountriesByCategorisation,
      accountUser: accountService.accountUser.value,
      countryOfResidency: this.countryOfResidency.value,
      clientCategorisation: this.clientCategorisation.value,
      value,
    });

    return value;
  });

  public isEnabledWithPublicTradingEnabled = computed(() => this.isEnabled.value && this.isPublicTradingEnabled.value);

  public reset = () => {
    Object.assign(this.state, getDefaultState());
  };
}

const futuresFeatureFlagService = new FuturesFeatureFlagService();

export default futuresFeatureFlagService;
